import { CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Col, Form, Input, message, Modal, Popconfirm, Row } from "antd";
import type { TableColumnsType } from "antd";
import { departmentApi } from "api/employee.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { useClientPaging } from "hooks/useClientPaging";
import { useSearchInput } from "hooks/useSearchInput";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Department } from "types/employee";
import { getTitle } from "utils";
import { can } from "utils/auth";

const departmentPath = "/department";

interface DepartmentForm {
  name: string;
  description?: string;
  sortOrder?: number | string;
}

export const DepartmentPage = observer(({ title = "" }) => {
  const [form] = Form.useForm<DepartmentForm>();
  const searchInput = useSearchInput();
  const search = searchInput.debounced;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [saving, setSaving] = useState(false);

  const paging = useClientPaging(departments);

  const canCreate = can(departmentPath, "CREATE");
  const canUpdate = can(departmentPath, "UPDATE");
  const canDelete = can(departmentPath, "DELETE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await departmentApi.findAll({ search });
      setDepartments(res.data.departments || []);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    form.resetFields();
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = useCallback(
    (department: Department) => {
      form.resetFields();
      form.setFieldsValue({
        name: department.name,
        description: department.description || "",
        sortOrder: department.sortOrder ? department.sortOrder : undefined,
      });
      setEditing(department);
      setModalOpen(true);
    },
    [form],
  );

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const data = {
      department: {
        name: values.name.trim(),
        description: (values.description || "").trim(),
        sortOrder: Math.max(0, Math.round(Number(values.sortOrder) || 0)),
      },
    };

    setSaving(true);
    try {
      if (editing) {
        await departmentApi.update(editing.id, data);
      } else {
        await departmentApi.create(data);
      }
      message.success(editing ? "Cập nhật thành công!" : "Tạo mới thành công!");
      setModalOpen(false);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (department: Department) => {
      await departmentApi.delete(department.id);
      message.success("Đã xóa phòng ban");
      fetchData();
    },
    [fetchData],
  );

  const columns = useMemo<TableColumnsType<Department>>(
    () => [
      {
        title: "Thứ tự",
        dataIndex: "sortOrder",
        key: "sortOrder",
        width: 90,
        align: "center",
        render: (value) => (value ? value : <span className="text-gray-300">--</span>),
      },
      { title: "Tên phòng ban", dataIndex: "name", key: "name", render: (value) => <span className="font-semibold">{value}</span> },
      { title: "Mô tả", dataIndex: "description", key: "description", render: (value) => <TableContent>{value || "--"}</TableContent> },
      {
        title: "Số nhân sự",
        dataIndex: "employeeCount",
        key: "employeeCount",
        width: 130,
        align: "right",
        render: (value) => value || 0,
      },
      ...(canUpdate || canDelete
        ? [
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: unknown, record: Department) => (
                <div className="flex items-center gap-1">
                  {canUpdate && (
                    <AppButton tone="light" aria-label="Sửa" onClick={() => openEdit(record)} style={{ padding: "2px 6px" }}>
                      <EditOutlined />
                    </AppButton>
                  )}
                  {canDelete && (
                    <Popconfirm title="Xóa phòng ban này?" okText="Xóa" cancelText="Hủy" onConfirm={() => handleDelete(record)}>
                      <AppButton tone="light" aria-label="Xóa" style={{ padding: "2px 6px" }}>
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </AppButton>
                    </Popconfirm>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canUpdate, canDelete, openEdit, handleDelete],
  );

  return (
    <div className="flex flex-col gap-4">
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchInput.value,
              width: 360,
              placeholder: "Tìm theo tên phòng ban",
              onChange: searchInput.onChange,
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Thêm phòng ban
                </AppButton>
              )
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Department> rowKey="id" loading={loading} pagination={false} dataSource={paging.pageRows} columns={columns} />
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />

      <Modal
        open={modalOpen}
        title={editing ? "Cập nhật phòng ban" : "Thêm phòng ban"}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Tên phòng ban" name="name" rules={[{ required: true, message: "Bắt buộc" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Số thứ tự"
                name="sortOrder"
                extra="Dùng để xếp danh sách nhân sự: số nhỏ đứng trước (VD: Giám đốc 1, Phó giám đốc 2). Để trống = xếp cuối."
              >
                <Input type="number" min={1} step={1} style={{ width: 160 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Mô tả" name="description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
});
